import { useSuspenseQuery } from "@tanstack/react-query"
import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection } from "@/areas/help-center"
import DownloadCard from "@/areas/help-center/components/DownloadCard/DownloadCard"
import { ReleasesDao } from "@/entities/releases"
import { AppName, Platform } from "@/entities/releases/releases.types"
import Article from "@/shared/layouts/Article/Article"

// Data Types
interface DownloadItem {
  name: string
  version: string
  releaseDate: string
  downloadUrl: string
  description?: string
  olderVersionsLink?: string
}

interface OraclePackageItem {
  name: string
  fileName: string
  includes: string[]
  downloadUrl: string
}

interface OracleIntegrationSection {
  title: string
  subtitle: string
  items: OraclePackageItem[]
  completePackageUrl: string
}

// Helper function to format date
const formatReleaseDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric"
  })
}

// Oracle Integration Files Data
const ORACLE_DLL_FILES: OracleIntegrationSection = {
  title: "Oracle Integration Files (.dll)",
  subtitle: "Select the correct .dll based on the customer's Drive-Thru lane configuration.",
  items: [
    {
      name: "Single-Lane.dll",
      fileName: "Single-Lane.dll",
      includes: ["StreamExtentionApp.dll"],
      downloadUrl: "/static/wiki/dll/StreamExtensionApp.dll"
    },
    {
      name: "Dual-Lane.dll",
      fileName: "Dual-Lane.dll",
      includes: ["StreamExtApp.dll"],
      downloadUrl: "/static/wiki/dll/streamExtApp.dll"
    }
  ],
  completePackageUrl: "https://placeholder-cdn.stream.com/oracle-dll-complete.zip"
}

const ORACLE_ZIP_FILES: OracleIntegrationSection = {
  title: "Oracle Integration Package (.zip)",
  subtitle: "Select the correct .dll based on the customer's Drive-Thru lane configuration.",
  items: [
    {
      name: "Single-Lane.zip",
      fileName: "Single-Lane.zip",
      includes: ["StreamExtentionApp.dll", "Oracle Setup PDF"],
      downloadUrl: "https://placeholder-cdn.stream.com/single-lane.zip"
    },
    {
      name: "Dual-Lane.zip",
      fileName: "Dual-Lane.zip",
      includes: ["StreamExtApp.dll", "Oracle Setup PDF"],
      downloadUrl: "https://placeholder-cdn.stream.com/dual-lane.zip"
    }
  ],
  completePackageUrl: "https://placeholder-cdn.stream.com/oracle-zip-complete.zip"
}

function GettingStarted() {
  const contentRef = useRef<HTMLDivElement>(null)

  // Fetch OCB App release data
  const { data: ocbAppData } = useSuspenseQuery({
    queryKey: [ReleasesDao.name, AppName.OcbApp],
    queryFn: async (): Promise<DownloadItem> => {
      try {
        const release = await ReleasesDao.getLatestRelease(AppName.OcbApp)
        const asset = release.assets.find(a => a.platform === Platform.Windows)

        if (!asset) {
          throw new Error("No Windows asset found for OCB App")
        }

        const downloadUrl = await ReleasesDao.getDownloadUrl(asset.s3Key)

        return {
          name: "OCB App",
          version: release.tagName,
          releaseDate: formatReleaseDate(release.publishedAt),
          downloadUrl: downloadUrl.url,
          description: release.releaseNotes,
          olderVersionsLink: "/help-center/older-versions/ocb-app"
        }
      } catch (error) {
        console.error("Failed to fetch OCB App data:", error)
        throw error
      }
    },
  })

  // Fetch Menu App release data
  const { data: menuAppData } = useSuspenseQuery({
    queryKey: [ReleasesDao.name, AppName.MenuApp],
    queryFn: async (): Promise<DownloadItem> => {
      try {
        const release = await ReleasesDao.getLatestRelease(AppName.MenuApp)
        const asset = release.assets.find(a => a.platform === Platform.Windows)

        if (!asset) {
          throw new Error("No Windows asset found for Menu App")
        }

        const downloadUrl = await ReleasesDao.getDownloadUrl(asset.s3Key)

        return {
          name: "Menu App",
          version: release.tagName,
          releaseDate: formatReleaseDate(release.publishedAt),
          downloadUrl: downloadUrl.url,
          description: release.releaseNotes,
          olderVersionsLink: "/help-center/older-versions/menu-app"
        }
      } catch (error) {
        console.error("Failed to fetch Menu App data:", error)
        throw error
      }
    },
  })

  // Fetch Cropper release data
  const { data: cropperData } = useSuspenseQuery({
    queryKey: [ReleasesDao.name, AppName.Cropper],
    queryFn: async (): Promise<DownloadItem> => {
      try {
        const release = await ReleasesDao.getLatestRelease(AppName.Cropper)

        // Cropper has empty assets array, download link is in releaseNotes
        let downloadUrl = ""

        if (release.assets.length > 0) {
          // Standard flow: find Windows asset
          const asset = release.assets.find(a => a.platform === Platform.Windows)
          if (asset) {
            const downloadUrlResponse = await ReleasesDao.getDownloadUrl(asset.s3Key)
            downloadUrl = downloadUrlResponse.url
          }
        } else {
          // Fallback: extract markdown link from releaseNotes
          const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/
          const match = release.releaseNotes.match(markdownLinkRegex)
          if (match && match[2]) {
            downloadUrl = match[2]
          }
        }

        if (!downloadUrl) {
          throw new Error("No download URL found for Cropper")
        }

        return {
          name: "Cropper",
          version: release.tagName,
          releaseDate: formatReleaseDate(release.publishedAt),
          downloadUrl: downloadUrl,
          description: release.releaseNotes,
          olderVersionsLink: "/help-center/older-versions/cropper"
        }
      } catch (error) {
        console.error("Failed to fetch Cropper data:", error)
        throw error
      }
    },
  })

  const STREAM_APPLICATIONS = [ocbAppData, menuAppData, cropperData]

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        logo="/static/wiki/downloadIcon.png"
        title="STREAM REQUIRED DOWNLOADS"
        subtitle="Download the latest versions of all applications and files required for your Stream setup."
        backPath="/help-center"
        backLabel="Help Center"
        contentRef={contentRef}
        hidePdfDownload={true}
      />

      <WikiSection id="stream-applications" title="STREAM APPLICATIONS">
        <Article>
          <div className="wiki-downloads-grid">
            {STREAM_APPLICATIONS.map((app, index) => (
              <DownloadCard
                key={index}
                name={app.name}
                version={app.version}
                releaseDate={app.releaseDate}
                downloadUrl={app.downloadUrl}
                description={app.description}
                olderVersionsLink={app.olderVersionsLink}
              />
            ))}
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="oracle-integration" title="ORACLE INTEGRATION FILES">
        <Article>
          <div className="wiki-download-subsection__description">
            These Files are required for Oracle POS Configuration.
          </div>

          <div className="wiki-downloads-two-col">
            {/* DLL Files Card */}
            <div className="wiki-oracle-card">
              <h3 className="wiki-oracle-card__title">{ORACLE_DLL_FILES.title}</h3>
              <p className="wiki-oracle-card__subtitle">{ORACLE_DLL_FILES.subtitle}</p>

              <div className="wiki-oracle-items-row">
                {ORACLE_DLL_FILES.items.map((item, index) => (
                  <div key={index} className="wiki-oracle-item">
                    <div className="wiki-oracle-item__name">{item.name}</div>
                    <div className="wiki-oracle-item__includes">
                      {item.includes.map((include, i) => (
                        <div key={i}>{include}</div>
                      ))}
                    </div>
                    <button
                      className="wiki-oracle-item__button"
                      onClick={() => window.open(item.downloadUrl, "_blank")}
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>

              <div className="wiki-download-complete">
                <button onClick={() => window.open(ORACLE_DLL_FILES.completePackageUrl, "_blank")}>
                  Download Complete Package
                </button>
                <p style={{ fontSize: "0.875rem", color: "#6b7280", marginTop: "0.5em", fontStyle: "italic" }}>
                  Includes Single & Dual .dll*
                </p>
              </div>
            </div>

            {/* ZIP Files Card */}
            <div className="wiki-oracle-card">
              <h3 className="wiki-oracle-card__title">{ORACLE_ZIP_FILES.title}</h3>
              <p className="wiki-oracle-card__subtitle">{ORACLE_ZIP_FILES.subtitle}</p>

              <div className="wiki-oracle-items-row">
                {ORACLE_ZIP_FILES.items.map((item, index) => (
                  <div key={index} className="wiki-oracle-item">
                    <div className="wiki-oracle-item__name">{item.name}</div>
                    <div className="wiki-oracle-item__includes">
                      {item.includes.map((include, i) => (
                        <div key={i}>{include}</div>
                      ))}
                    </div>
                    <button
                      className="wiki-oracle-item__button"
                      onClick={() => window.open(item.downloadUrl, "_blank")}
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>

              <div className="wiki-download-complete">
                <button onClick={() => window.open(ORACLE_ZIP_FILES.completePackageUrl, "_blank")}>
                  Download Complete Package
                </button>
                <p style={{ fontSize: "0.875rem", color: "#6b7280", marginTop: "0.5em", fontStyle: "italic" }}>
                  Includes Single & Dual .dll + Setup File*
                </p>
              </div>
            </div>
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default GettingStarted
